// @flow
import { getLoadTimeForCatalogItems, isCatalogItemsLoading } from './loaded';
import { loadBatchEnd, loadBatchStart } from './fetchBatch';
import api from '../api/item';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getAuthToken } from '../modules/user';
import { getCatalog } from './catalog';
import { getCatalogItems } from '../modules/filteredItems';
import { getDeployment } from '../modules/config';
import type { GlobalState } from '../rootReducer';
import { LOAD_CATALOG_AD_SUCCESS } from '../modules/catalogAd';
import { LOAD_CATALOG_ITEMS_SUCCESS } from '../modules/catalogItems';
import { LOAD_HOME_DATA_SUCCESS } from '../modules/home';
import { LOAD_STOREFRONT_ITEMS_SUCCESS } from '../modules/storefrontPage';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_ITEMS_FAIL = 'la/domain/items/LOAD_FAIL';
export const LOAD_ITEMS_REQUEST = 'la/domain/items/LOAD_REQUEST';
export const LOAD_ITEMS_SUCCESS = 'la/domain/items/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');
const FETCH_SET_SIZE = 128;

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_ITEMS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta.itemIds),
            };
        case LOAD_ITEMS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_CATALOG_AD_SUCCESS:
        case LOAD_CATALOG_ITEMS_SUCCESS:
        case LOAD_HOME_DATA_SUCCESS:
        case LOAD_ITEMS_SUCCESS:
        case LOAD_STOREFRONT_ITEMS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.items) {
                action.payload.items.forEach(item => {
                    existing[item.itemId] = { ...item };
                    loaded[item.itemId] = time;
                    loading = difference(loading, [item.itemId]);
                });
            }
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.item;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getItem = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});

export const getLoadTimeForItem = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

export const isItemLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

export const getItemIdsForCatalog = createSelector([byIdSelector, idSelector], (byId, id) =>
    Object.keys(byId)
        .map(key => byId[key])
        .filter(item => item.catalogId === id)
        .map(item => item.itemId)
);

export const getItemIdsForSeller = createSelector([byIdSelector, idSelector], (byId, id) =>
    Object.keys(byId)
        .map(key => byId[key])
        .filter(item => item.sellerId === id)
        .map(item => item.itemId)
);

const shouldFetchItem = (state, itemId) => {
    if (!itemId) return false;
    const item = getItem(state, itemId);
    if (item) {
        const loaded = getLoadTimeForItem(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    const loading = isItemLoading(state, itemId);
    return !loading;
};

const whichItemsNeeded = (state, itemIds) => itemIds.filter(itemId => shouldFetchItem(state, itemId));

export const shouldFetchItemsForCatalog = (
    state: GlobalState,
    catalogId: number,
    cursor?: any,
    page?: number,
    itemId?: number,
    count?: number | string
) => {
    if (!catalogId) return false;
    const loaded = getLoadTimeForCatalogItems(state, catalogId);
    const time = Date.now();
    const diff = time - loaded;

    const { lotsListed } = getCatalog(state, catalogId);
    const totalItemsCount = getItemIdsForCatalog(state, catalogId).length;
    if (totalItemsCount === lotsListed) {
        if (diff < REDUX_STORE_TIME) return false;
        const loading = isCatalogItemsLoading(state, catalogId);
        return !loading;
    }

    return true;
};

export const getNextAndPreviousItems = (state: GlobalState, itemId: number) => {
    const { catalogId, index } = getItem(state, itemId);

    const { items } = getCatalogItems(state, catalogId, {
        sort: 'LOT_NUMBER_LOWEST_FIRST',
    });
    const { catalog } = items?.[0] || {};
    const { lotsListed } = catalog || {};

    let previousItem = {};
    let nextItem = {};

    if (index === 0) {
        nextItem = items.find(({ item }) => item.index === index + 1);
    } else if (index === lotsListed - 1) {
        previousItem = items.find(({ item }) => item.index === index - 1);
    } else {
        previousItem = items.find(({ item }) => {
            return item.index === index - 1;
        });
        nextItem = items.find(({ item }) => {
            return item.index === index + 1;
        });
    }

    const next = nextItem?.item || {};
    const previous = previousItem?.item || {};

    return { next, previous };
};

/* ACTION CREATORS */
const loadItems = (itemIds, loadingId, lastRound) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: itemIds,
            type: LOAD_ITEMS_REQUEST,
        });
        const response = await api.fetchItemsByIds({ authToken, deployment, itemIds });
        dispatch({
            meta: { actionTime: Date.now(), itemIds },
            payload: response.data,
            type: LOAD_ITEMS_SUCCESS,
        });
        if (lastRound) {
            dispatch(loadBatchEnd(loadingId));
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { itemIds },
            payload: error,
            type: LOAD_ITEMS_FAIL,
        });
        dispatch(loadBatchEnd(loadingId));
    }
};

const loadItemsInSets = (ids, setSize, loadingId) => async dispatch => {
    if (ids.length > setSize) {
        const newSize = Math.min(8192, setSize * 2);
        return dispatch(loadItems(ids.slice(0, setSize), loadingId, false)).then(() => {
            dispatch(loadItemsInSets(ids.slice(setSize), newSize, loadingId));
        });
    }
    return dispatch(loadItems(ids, loadingId, true));
};

export const fetchItemsIfNeeded = (itemIds: number[], loadingId: string = 'none') => async (
    dispatch: Function,
    getState: Function
) => {
    const needed = whichItemsNeeded(getState(), itemIds);
    if (needed.length) {
        dispatch(loadBatchStart(loadingId));
        return dispatch(loadItemsInSets(needed, FETCH_SET_SIZE, loadingId));
    }
    return Promise.resolve();
};
